/**
 * URL search contract for the Team Leader Exam Workspace (`/team-leader/exams/$examId`).
 *
 * The URL is the single source of truth for which tab is open, which student's Sheet is showing,
 * and every roster filter/page — so a workspace state is refresh-safe, shareable and restored by
 * Back/Forward without any component holding a duplicate copy in React state.
 *
 * Only parameters the backend actually honours are represented here. In particular:
 *
 * - **No `sort`/`dir`.** `GetExamDetailAsync` hardcodes `OrderBy(UserId).ThenBy(StartedAt)` and
 *   ignores the `SortBy`/`SortDir` fields it accepts. A sort control would be a lie, so the
 *   Attempts tab ships none and the schema has nowhere to put one.
 * - **`status` is real as of Phase 1.5-B**, filtered server-side *before* paging. Its vocabulary is
 *   `ExamSessionStatus` names (see `ATTEMPT_STATUSES`); anything else is ignored by the server, so
 *   anything else is dropped here rather than written into a request that would silently no-op.
 * - **`student` ≠ `studentId`.** `studentId` is the pre-existing *roster filter* — the server
 *   intersects the student set with it, narrowing the table to one person. `student` is the Student
 *   Sheet target and is never sent to the server. Collapsing them into one name would mean opening
 *   a student silently emptied the roster behind the Sheet, and closing it could not tell a peek
 *   from a deliberate filter.
 */
import { isAttemptStatus, type AttemptStatus } from "@/lib/exam-moderation";

/**
 * A marker written into history state when the Sheet is opened by a click, so closing it knows
 * whether there is an in-app entry to go back to. Reading `history.length` cannot answer that —
 * it counts entries from other origins and behaves differently across browsers.
 */
declare module "@tanstack/history" {
  interface HistoryState {
    tlStudentSheetPushed?: boolean;
  }
}

export const EXAM_WORKSPACE_TABS = ["overview", "attempts", "questions"] as const;
export type ExamWorkspaceTab = (typeof EXAM_WORKSPACE_TABS)[number];

export const DEFAULT_EXAM_TAB: ExamWorkspaceTab = "overview";
export const DEFAULT_ATTEMPTS_PAGE_SIZE = 25;
/** `GetExamDetailAsync` applies `Math.Min(filter.PageSize, 100)`; clamp here so the URL never lies. */
export const MAX_ATTEMPTS_PAGE_SIZE = 100;

/**
 * The raw, URL-faithful shape. Every field is optional: absent means "default", and a default is
 * never written back into the URL, so a freshly-opened workspace has a clean address.
 */
export interface ExamWorkspaceSearch {
  tab?: ExamWorkspaceTab;
  /** Student Sheet target. Client-only — never sent to the server. */
  student?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  /** Roster filter: narrow the table to one student (backend `StudentId`). */
  studentId?: string;
  /** Roster filter: attempt status (backend `Status`, honoured since Phase 1.5-B). */
  status?: AttemptStatus;
  /** Roster filter: narrow to a managed group (backend `GroupId`). */
  groupId?: string;
}

/** Fully-resolved view of the above, with defaults applied. What components actually read. */
export interface ResolvedExamWorkspaceSearch extends ExamWorkspaceSearch {
  tab: ExamWorkspaceTab;
  page: number;
  pageSize: number;
}

function asTrimmedString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

function asPositiveInt(value: unknown, max?: number): number | undefined {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  if (!Number.isFinite(n)) return undefined;
  const i = Math.floor(n);
  if (i < 1) return undefined;
  return max ? Math.min(i, max) : i;
}

function isExamWorkspaceTab(value: unknown): value is ExamWorkspaceTab {
  return typeof value === "string" && (EXAM_WORKSPACE_TABS as readonly string[]).includes(value);
}

/**
 * `validateSearch` for the workspace route. Unrecognised values are *dropped*, not preserved: an
 * unknown `tab` falls back to Overview, a `status` outside the backend vocabulary is discarded
 * rather than sent, and `page=0`/`page=-3` become "no page" instead of an invalid request.
 */
export function validateExamWorkspaceSearch(raw: Record<string, unknown>): ExamWorkspaceSearch {
  return {
    tab: isExamWorkspaceTab(raw.tab) ? raw.tab : undefined,
    student: asTrimmedString(raw.student),
    page: asPositiveInt(raw.page),
    pageSize: asPositiveInt(raw.pageSize, MAX_ATTEMPTS_PAGE_SIZE),
    search: asTrimmedString(raw.search),
    studentId: asTrimmedString(raw.studentId),
    status: isAttemptStatus(asTrimmedString(raw.status))
      ? (asTrimmedString(raw.status) as AttemptStatus)
      : undefined,
    groupId: asTrimmedString(raw.groupId),
  };
}

export function resolveExamWorkspaceSearch(
  search: ExamWorkspaceSearch,
): ResolvedExamWorkspaceSearch {
  return {
    ...search,
    tab: search.tab ?? DEFAULT_EXAM_TAB,
    page: search.page ?? 1,
    pageSize: search.pageSize ?? DEFAULT_ATTEMPTS_PAGE_SIZE,
  };
}

/**
 * Normalises a next-search object so defaults drop out of the URL entirely (TanStack omits
 * `undefined`). Without this every tab click would append `page=1&pageSize=25`.
 */
export function stripExamWorkspaceDefaults(next: ExamWorkspaceSearch): ExamWorkspaceSearch {
  return {
    ...next,
    tab: next.tab === DEFAULT_EXAM_TAB ? undefined : next.tab,
    page: next.page && next.page > 1 ? next.page : undefined,
    pageSize:
      next.pageSize && next.pageSize !== DEFAULT_ATTEMPTS_PAGE_SIZE ? next.pageSize : undefined,
    search: next.search || undefined,
    student: next.student || undefined,
    studentId: next.studentId || undefined,
    status: next.status || undefined,
    groupId: next.groupId || undefined,
  };
}

/** Whether any roster *filter* is active. Paging and the Sheet target are not filters. */
export function hasActiveAttemptFilters(search: ExamWorkspaceSearch): boolean {
  return !!(search.search || search.studentId || search.status || search.groupId);
}

/** The roster filters only, for building the request params and the CSV export scope. */
export function attemptFilterParams(search: ExamWorkspaceSearch) {
  return {
    search: search.search || undefined,
    groupId: search.groupId || undefined,
    studentId: search.studentId || undefined,
    status: search.status || undefined,
  };
}
